import { useEffect, useRef, useState, type ReactNode } from 'react';
import axios from 'axios';
import { Check, Loader2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { IS_DEMO, IS_LOCKED } from '../../apiBase';
import {
  checkOverlaps, collideShotSrc, collideShotsOf, collisionsFixOf, collisionsPrompt, fixCollisions,
  newRunToken, overlapsOf, stopWritten, writtenInputsOf, type RunProgress,
} from './openaiBlenderApi';
import { listingImageSrc } from './panels/NoticeProductImages';
import { directionLabel } from './viewDirections';
import { bumpProductRuns } from './productRuns';
import { CallBadge } from './CallBadge';
import { CollisionsBox, CollisionsTable } from './CollisionsTable';
import { reasoningTooltip } from './ReasoningBadge';
import { falError, type Job3D } from './threeDApi';
import { createDocValueStore } from './docStore';

// WHERE TWO PARTS OF THE PRODUCT FILL THE SAME SPACE, as Blender measured it when the version was
// built (see written_checks.overlap_checks, which says the same to the next pass). Solid parts
// meet at their surfaces: a rail built 20 mm into its leg looks right in every render, and puts
// every hole Find holes measures across that joint off by as much. So it is a table, pair by pair,
// with how deep — the Assembly step's Clear / Overlaps, for a product standing still.
//
// A VERSION BUILT BEFORE THE CHECK IS CHECKED ON SIGHT, on its own file (see checkOverlaps): a few
// seconds of Blender on the server and no call, kept on the version so it is asked once. Not in
// the demo, which saves nothing.
//
// AND PUT RIGHT FROM HERE: Fix collisions asks a model to rewrite the script so the listed pairs only
// touch, fits what is left and shallow — cut away from the part that gives way, in Blender — and
// saves the result as ONE new version, refused if any other mesh moved (see fix_written_collisions).
// Its badge shows what the call sends, and on a version it made, what the model thought and wrote.
//
// A ROW IS A SWITCH: pressed, its shared solid is laid over the model in red (see
// AssemblyView.overlays). The set is the caller's, which hands it to the viewer too. The table is
// the covers' too — see CollisionsTable.
// WHERE ITS BADGE IS DRAWN: in the Collisions box, under its title (Louis 2026-10-28). It was in
// the phase's row of call badges, after 3D model generation, a screen away from the table it acts on.

// A FIX RUNNING, by manual — kept outside the panel so Phase 5's pill can say it is (see
// phaseProgress).
const fixingStore = createDocValueStore<boolean>(false);
export const useFixingCollisions = fixingStore.useValue;

export function ProductOverlaps({ docId, job, locked, onFixed, picked, onPick, onPickAll }: {
  docId: string;
  job: Job3D;
  // Which rows are laid over the model, by their solid's number (Overlap.mesh).
  picked: ReadonlySet<number>;
  onPick: (mesh: number) => void;
  // All of these on, and nothing else — [] for none.
  onPickAll: (meshes: number[]) => void;
  // The phase is locked: nothing is spent.
  locked?: boolean;
  // The version the fix saved, to be shown.
  onFixed: (job: Job3D) => void;
}) {
  const { t } = useLanguage();
  const found = overlapsOf(job);
  const made = collisionsFixOf(job);
  const fixing = useFixingCollisions(docId);
  const setFixing = (on: boolean) => fixingStore.set(docId, on);
  const [fixError, setFixError] = useState('');
  // WHY THE LAST RUN STOPPED WITH COLLISIONS LEFT — no progress, or the pass limit. See _fix_loop.
  const [fixNote, setFixNote] = useState('');
  const [progress, setProgress] = useState<RunProgress | null>(null);
  const run = useRef<{ token: string; abort: AbortController } | null>(null);
  const fix = async () => {
    const stop = { token: newRunToken(), abort: new AbortController() };
    run.current = stop;
    setFixing(true);
    setFixError('');
    setFixNote('');
    setProgress(null);
    let seen = 0;
    try {
      const { job: last, note } = await fixCollisions(job.id, { token: stop.token, signal: stop.abort.signal },
        (p) => {
          setProgress(p);
          // A PASS SAVED IS A VERSION ON THE PAGE, as soon as it is one.
          if (p.saved.length > seen) {
            seen = p.saved.length;
            bumpProductRuns(docId);
          }
        });
      if (note) setFixNote(note);
      if (last) onFixed(last);
      else bumpProductRuns(docId);
    } catch (e) {
      if (!axios.isCancel(e)) setFixError(falError(e, t('notice.overlaps.fixFailed')));
      bumpProductRuns(docId);
    } finally {
      run.current = null;
      setFixing(false);
      setProgress(null);
    }
  };
  // STOP: the call in flight is cancelled at its next poll and no pass after it is made; the
  // versions already saved stay. Then the page stops waiting.
  const halt = () => {
    const now = run.current;
    if (!now) return;
    void stopWritten(now.token).catch(() => {});
    now.abort.abort();
  };
  const off = !!locked || IS_LOCKED || fixing || !found?.length;
  // THE CALL, IN THE BOX IT ACTS ON. Its Run is the press, and its Stop; what the last run said
  // stays under the table it is about.
  const badge = (
    <CallBadge
      title={t('notice.overlaps.fix')}
      named
      run={{
        // WHAT IT IS DOING — the call, the fit, or the check of the version they made.
        label: fixing && progress
          ? `${t('notice.overlaps.fixing')} · ${t(progress.stage === 'checking' ? 'notice.overlaps.stageChecking'
            : progress.stage === 'fitting' ? 'notice.overlaps.stageFitting' : 'notice.overlaps.stageFixing')}`
          : t('notice.overlaps.fix'),
        onRun: () => void fix(), busy: fixing, disabled: off,
        // STOP: the call in flight is cancelled at its next poll; the versions saved stay.
        onStop: halt,
      }}
      promptLabel={t('notice.overlaps.fixPrompt')}
      // WITH THE PHOTOGRAPHS IT SENDS: the ones this version was built from, in that order.
      load={async () => ({
        ...(await collisionsPrompt(job.id)),
        urls: writtenInputsOf(job).map((one) => ({
          url: listingImageSrc(one.imageId),
          caption: one.view ? directionLabel(one.view) : undefined,
        })),
      })}
      // THEN THE RENDERS, on a row of their own: see-through, the overlaps red, sent after the
      // photographs. A second kind of picture, which is what `more` breaks the strip for.
      more={async () => collideShotsOf(job).map((name) => ({
        url: collideShotSrc(job.id, name),
        caption: `${t('notice.overlaps.render')} · ${directionLabel(name)}`,
      }))}
      revision={String(job.id)}
      answer={made && !fixing ? {
        label: t('notice.cat.thinking'),
        content: reasoningTooltip(made.reasoning, made.said, t, made.call),
      } : undefined}
    />
  );
  const controls = (
    <div className="flex flex-wrap items-center justify-center gap-2">
      {fixError && <span className="text-[11px] text-rose-500">{fixError}</span>}
      {fixNote && <span className="basis-full text-center text-[11px] text-amber-600">{fixNote}</span>}
    </div>
  );
  const [checking, setChecking] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const asked = useRef<number | null>(null);
  useEffect(() => {
    if (found || IS_DEMO || asked.current === job.id) return;
    asked.current = job.id;
    setChecking(true);
    setFailed(null);
    checkOverlaps(job.id)
      .then(() => bumpProductRuns(docId))
      .catch((e) => setFailed((axios.isAxiosError(e) && e.response?.data?.error) || String(e)))
      .finally(() => setChecking(false));
  }, [found, job.id, docId]);
  const titled = (body: ReactNode) => <CollisionsBox call={badge}>{body}</CollisionsBox>;
  if (!found) {
    return titled(
      <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
        {checking && <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}
        {checking ? t('notice.overlaps.checking')
          : failed ? <span className="text-amber-600">{failed}</span>
            : t('notice.overlaps.unchecked')}
      </div>,
    );
  }
  if (!found.length) {
    return titled(
      <div className="flex flex-wrap items-center justify-center gap-2 text-sm font-semibold text-blue-600">
        <Check className="h-4 w-4 shrink-0" />
        {t('notice.overlaps.none')}
        {/* The call that got it here, where one did. */}
        {made && controls}
      </div>,
    );
  }
  return titled(
    <>
      <CollisionsTable
        rows={found.map((o) => ({
          key: o.mesh != null ? String(o.mesh) : `${o.a}|${o.b}`,
          a: o.a, b: o.b, depth: o.depth, mm: o.mm, pickable: o.mesh != null,
        }))}
        picked={new Set([...picked].map(String))}
        onPick={(key) => onPick(Number(key))}
        onPickAll={(keys) => onPickAll(keys.map(Number))}
      />
      {controls}
    </>,
  );
}
